import { z } from 'zod'
import {
  deckClosesInMinutesSchema,
  deckModeSchema,
  deckSeedItemsInputSchema,
  deckTitleInputSchema,
  swipeDeckMessageMetadataSchema,
} from './deck'
import { messageMentionSchema, messageReactionSchema, messageReplyPreviewSchema } from './dm'
import { planVoteMetadataSchema } from './plan-vote'

export const chatSystemKindValues = [
  'created_plan',
  'deleted_plan',
  'joined_plan',
  'joined_waitlist',
  'left_plan',
  'removed_participant',
  'made_group',
  'plan_recap',
  'added_stop',
  'updated_stop',
  'removed_stop',
  'plan_updated',
  'deck_resolved',
  'deck_option_added',
  'deck_opened',
  'film_ready',
] as const

export const planUpdateFieldValues = [
  'name',
  'description',
  'date',
  'timeStart',
  'timeEnd',
  'location',
  'category',
  'planType',
] as const

/** One changed plan field; `value` is the raw stored value (ISO date, HH:mm, enum key) or null when cleared. */
export const planUpdateFieldSchema = z.object({
  field: z.enum(planUpdateFieldValues),
  value: z.string().nullable(),
})

export type PlanUpdateField = z.infer<typeof planUpdateFieldSchema>

/**
 * Structured system event so the client can localize it and make it
 * viewer-relative ("You joined" vs "{name} joined"). The message body is only
 * an English fallback for old clients / accessibility.
 */
export const chatSystemMetaSchema = z.object({
  kind: z.enum(chatSystemKindValues),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  targetId: z.string().nullable(),
  targetName: z.string().nullable(),
  /** `plan_updated` only: the fields that changed, for a localized one-line summary. */
  fields: z.array(planUpdateFieldSchema).optional(),
  /** `film_ready` only: how many photos the group's film has; `targetId` is the event. */
  count: z.number().int().nonnegative().optional(),
})

export type ChatSystemMeta = z.infer<typeof chatSystemMetaSchema>

/**
 * Payload for the tappable poll/list cards the backend posts into a plan chat
 * when a poll is created or a Mitbringliste item is added. Carries the preview
 * the bubble renders plus the id it deep-links to, so no extra fetch is needed.
 */
export const chatCardMetaSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('poll'),
    pollId: z.string().uuid(),
    question: z.string(),
    optionPreview: z.array(z.string()),
    optionCount: z.number().int(),
  }),
  z.object({
    kind: z.literal('list'),
    listItemId: z.string().uuid(),
    text: z.string(),
    quantity: z.number().int(),
  }),
  planVoteMetadataSchema.extend({ kind: z.literal('plan_vote') }),
  swipeDeckMessageMetadataSchema.extend({ kind: z.literal('swipe_deck') }),
  z.object({
    kind: z.literal('event_share'),
    eventId: z.string().uuid(),
    title: z.string(),
    coverImageUrl: z.string().nullable(),
    locationName: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('listing_share'),
    listingId: z.string().uuid(),
    name: z.string(),
    coverImageUrl: z.string().nullable(),
    locationName: z.string().nullable(),
  }),
])

export type ChatCardMeta = z.infer<typeof chatCardMetaSchema>

export const chatAgentActionKindValues = [
  'list_item',
  'poll',
  'deck',
  'vote',
  'plan_fields',
] as const

/** One thing the plan assistant did because of a message: the item/poll/deck it created
 * (`text` = its label) or the plan fields it changed (`fields`). */
export const chatAgentActionSchema = z.object({
  kind: z.enum(chatAgentActionKindValues),
  text: z.string().nullable(),
  fields: z.array(z.enum(planUpdateFieldValues)).optional(),
  /** On a `list_item`: the writer took over an item already on the list; an undo only gives it back. */
  claimed: z.boolean().optional(),
})

export type ChatAgentAction = z.infer<typeof chatAgentActionSchema>

/**
 * Set on a message the plan assistant acted on. The client collapses the bubble while
 * `undoneAt` is null and offers to undo; after an undo it renders as a normal message.
 */
export const chatMessageAgentSchema = z.object({
  actions: z.array(chatAgentActionSchema),
  handledAt: z.string(),
  undoneAt: z.string().nullable(),
})

export type ChatMessageAgent = z.infer<typeof chatMessageAgentSchema>

export const chatMessageDtoSchema = z.object({
  id: z.string().uuid(),
  /** Stable client-side key so an optimistic message doesn't re-key (blink) on send. */
  clientId: z.string().optional(),
  planId: z.string().uuid(),
  senderId: z.string().uuid().nullable(),
  senderName: z.string().nullable(),
  senderAvatarUrl: z.string().nullable(),
  content: z.string().max(2000),
  /** Text the sender wrote on an attachment. `content` carries a kind label for
   * builds too old to render the attachment, so caption from here instead. */
  caption: z.string().nullable().optional(),
  messageType: z.enum([
    'user',
    'system',
    'image',
    'voice',
    'poll_card',
    'list_card',
    'plan_vote',
    'swipe_deck',
    'listing_share',
  ]),
  imageUrl: z.string().nullable(),
  imageWidth: z.number().int().nullable(),
  imageHeight: z.number().int().nullable(),
  /** Signed URL of a voice message's audio (messageType `voice`), else null. Optional so
   * existing optimistic-message builders stay valid. */
  audioUrl: z.string().nullable().optional(),
  /** Voice-message duration in milliseconds. Optional so existing optimistic-message
   * builders stay valid. */
  durationMs: z.number().int().nullable().optional(),
  /** Normalized 0–1 amplitude samples for the voice waveform, or null. Optional so
   * existing optimistic-message builders stay valid. */
  waveform: z.array(z.number()).nullable().optional(),
  viewOnce: z.boolean(),
  viewed: z.boolean(),
  system: chatSystemMetaSchema.nullable(),
  card: chatCardMetaSchema.nullable(),
  createdAt: z.string(),
  /** When a disappearing message self-deletes; null for permanent messages. */
  expiresAt: z.string().nullable(),
  /** Set when deleted for everyone; the client renders a tombstone instead of content. */
  deletedAt: z.string().nullable(),
  /** Set when the sender edited the message (the bubble shows an "Edited" label); null
   * otherwise. Optional so existing optimistic-message builders stay valid. */
  editedAt: z.string().nullable().optional(),
  /** Compact quote of the replied-to message, or null when this isn't a reply / the
   * parent was deleted. Optional so existing optimistic-message builders stay valid. */
  replyTo: messageReplyPreviewSchema.nullable().optional(),
  /** Per-emoji reaction buckets aggregated for the viewer. Defaults to [] server-side;
   * optional so existing optimistic-message builders stay valid. */
  reactions: z.array(messageReactionSchema).optional(),
  /** Resolved @mentions in the body (handle → user id), so the bubble highlights and
   * links them. Optional so existing optimistic-message builders stay valid. */
  mentions: z.array(messageMentionSchema).optional(),
  /** Set when this message is pinned in the plan chat; null otherwise. Optional so
   * existing optimistic-message builders stay valid. */
  pinnedAt: z.string().nullable().optional(),
  /** What the plan assistant did with this message, or null. Optional so existing
   * optimistic-message builders stay valid. */
  agent: chatMessageAgentSchema.nullable().optional(),
  /** The prompt a quiet plan was created from: the creator's request to the assistant, which
   * only its sender ever receives. Absent on every other message. */
  privateToSender: z.boolean().optional(),
})

export type ChatMessageDto = z.infer<typeof chatMessageDtoSchema>

/** Shared extras on every plan-chat send kind (merged, not intersected — OpenAPI/Hono keep the fields). */
const sendMessageExtrasSchema = z.object({
  replyToMessageId: z.string().uuid().optional(),
  /** Client-generated id so the realtime echo can replace the optimistic row. */
  clientId: z.string().min(1).max(64).optional(),
})

/** Optional reply target, valid on every plan-chat message kind (any bubble can be quoted). */
export const sendMessageInputSchema = z.union([
  z
    .object({
      content: z.string().min(1).max(2000),
      /** People tagged on the message without an `@handle` in the text; must belong to the plan. */
      mentionUserIds: z.array(z.string().uuid()).max(50).optional(),
      /** Roughly where the writer is, so the plan assistant searches near them; never stored. */
      area: z
        .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
        .optional(),
      /**
       * Exactly where the writer is right now, sent only when they switched on "Hier" for this
       * message. Used once to place this plan there; never stored anywhere else.
       */
      here: z
        .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
        .optional(),
    })
    .merge(sendMessageExtrasSchema),
  z
    .object({
      kind: z.literal('image'),
      imagePath: z.string(),
      width: z.number().int(),
      height: z.number().int(),
      caption: z.string().max(2000).optional(),
      viewOnce: z.boolean().optional(),
    })
    .merge(sendMessageExtrasSchema),
  z
    .object({
      kind: z.literal('voice'),
      audioPath: z.string(),
      durationMs: z.number().int().min(0),
      waveform: z.array(z.number()).max(256).optional(),
    })
    .merge(sendMessageExtrasSchema),
  z
    .object({
      kind: z.literal('plan_vote'),
      eventId: z.string().uuid().optional(),
      planId: z.string().uuid().optional(),
    })
    .merge(sendMessageExtrasSchema),
  z
    .object({
      kind: z.literal('listing_share'),
      listingId: z.string().uuid(),
    })
    .merge(sendMessageExtrasSchema),
  z
    .object({
      kind: z.literal('swipe_deck'),
      items: deckSeedItemsInputSchema,
      mode: deckModeSchema.optional(),
      title: deckTitleInputSchema.optional(),
      closesInMinutes: deckClosesInMinutesSchema.optional(),
      allowOthersToAdd: z.boolean().optional(),
    })
    .merge(sendMessageExtrasSchema),
])

export type SendMessageInput = z.infer<typeof sendMessageInputSchema>

/**
 * Merge a realtime `new_message` into the local list without duplicating an
 * optimistic row that still carries a temporary id.
 */
export function mergeIncomingPlanChatMessage<T extends { id: string; clientId?: string }>(
  messages: T[],
  incoming: T,
): T[] {
  if (messages.some((m) => m.id === incoming.id)) return messages
  if (incoming.clientId) {
    const idx = messages.findIndex(
      (m) => m.clientId === incoming.clientId || m.id === incoming.clientId,
    )
    if (idx >= 0) {
      const next = messages.slice()
      const prior = next[idx]
      if (!prior) return [...messages, incoming]
      next[idx] = {
        ...incoming,
        clientId: incoming.clientId ?? prior.clientId,
      }
      return next
    }
  }
  return [...messages, incoming]
}

/**
 * Apply the HTTP send response: give the optimistic row the server's id (and time, when
 * given), or drop it if the realtime echo already landed with the server id.
 */
export function resolveOptimisticPlanChatMessage<T extends { id: string; clientId?: string }>(
  messages: T[],
  optimisticId: string,
  server: { id: string; clientId?: string; createdAt?: string },
): T[] {
  // Echo already landed with the server id — drop only the temporary row.
  // Do not filter by clientId: the merged echo keeps the same clientId.
  if (messages.some((m) => m.id === server.id)) {
    return messages.filter((m) => m.id !== optimisticId)
  }
  return messages.map((m) =>
    m.id === optimisticId || m.clientId === optimisticId
      ? {
          ...m,
          id: server.id,
          clientId: server.clientId ?? m.clientId ?? optimisticId,
          ...(server.createdAt ? { createdAt: server.createdAt } : {}),
        }
      : m,
  )
}

export type ReconciledChatMessage = {
  id: string
  clientId?: string
  content: string | null
  createdAt: string
  deletedAt?: string | null
  editedAt?: string | null
  expiresAt?: string | null
  agent?: ChatMessageAgent | null
}

const PENDING_MESSAGE_ID = /^(optimistic|temp)-/

function agentProgress(agent: ChatMessageAgent | null | undefined): [number, number] {
  if (!agent) return [0, 0]
  return agent.undoneAt ? [2, Date.parse(agent.undoneAt)] : [1, Date.parse(agent.handledAt)]
}

function isLaterAgent(
  a: ChatMessageAgent | null | undefined,
  b: ChatMessageAgent | null | undefined,
): boolean {
  const [stageA, atA] = agentProgress(a)
  const [stageB, atB] = agentProgress(b)
  return stageA > stageB || (stageA === stageB && atA > atB)
}

function laterRow<T extends ReconciledChatMessage>(onScreen: T, fetched: T): T {
  if (onScreen.deletedAt && !fetched.deletedAt) return onScreen
  let row = fetched
  if (onScreen.clientId && !row.clientId) row = { ...row, clientId: onScreen.clientId }
  if (isLaterAgent(onScreen.agent, row.agent)) row = { ...row, agent: onScreen.agent }
  if ((onScreen.editedAt ?? '') > (row.editedAt ?? '')) {
    row = { ...row, content: onScreen.content, editedAt: onScreen.editedAt }
  }
  return row
}

/**
 * Merges a refetched page of a plan chat into the rows on screen. The page may have been read
 * before a realtime update landed, so it never takes one back: rows newer than the page and sends
 * still on their way stay unless they ran out, and a row keeps its client key, a deletion, a later
 * edit and the assistant's note or undo.
 */
export function reconcilePlanChatPage<T extends ReconciledChatMessage>(
  onScreen: T[],
  fetched: T[],
  now: number = Date.now(),
): T[] {
  if (onScreen.length === 0) return fetched
  const byId = new Map(onScreen.map((m) => [m.id, m]))
  const fetchedIds = new Set(fetched.map((m) => m.id))
  const newestFetched = Math.max(...fetched.map((m) => Date.parse(m.createdAt)))
  const merged = fetched.map((m) => {
    const seen = byId.get(m.id)
    return seen ? laterRow(seen, m) : m
  })
  const newer = onScreen.filter(
    (m) =>
      !fetchedIds.has(m.id) &&
      (PENDING_MESSAGE_ID.test(m.id) || Date.parse(m.createdAt) > newestFetched) &&
      !(m.expiresAt && Date.parse(m.expiresAt) <= now),
  )
  return newer.length === 0 ? merged : [...merged, ...newer]
}

export type ChatPage<T> = { messages: T[]; nextCursor: string | null; hasMore: boolean }

/** The `nextCursor` a message page ends with when `row` is its oldest message. */
export function messageCursorBefore(row: { createdAt: string; id: string }): string {
  return btoa(JSON.stringify({ createdAt: row.createdAt, id: row.id }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

function olderHistory<T extends ReconciledChatMessage>(
  onScreen: T[],
  fetched: T[],
  now: number,
): T[] {
  const fetchedIds = new Set(fetched.map((m) => m.id))
  const oldest = fetched.reduce<T | undefined>(
    (first, m) => (!first || Date.parse(m.createdAt) < Date.parse(first.createdAt) ? m : first),
    undefined,
  )
  // The page must reach into the loaded rows from its oldest end; a message both hold only because
  // it came in live (or was just sent) would join old history to the page across a gap.
  if (!oldest || !onScreen.some((m) => m.id === oldest.id)) return []
  const oldestFetched = Date.parse(oldest.createdAt)
  return onScreen.filter(
    (m) =>
      !fetchedIds.has(m.id) &&
      !PENDING_MESSAGE_ID.test(m.id) &&
      Date.parse(m.createdAt) < oldestFetched &&
      !(m.expiresAt && Date.parse(m.expiresAt) <= now),
  )
}

/**
 * Merges a refetched newest page into a chat thread (DM, group or plan) like
 * {@link reconcilePlanChatPage}. While the page meets the loaded rows and the thread goes on past
 * it, the older rows a reader scrolled back to stay, with their cursor; after a gap the page
 * starts the thread over.
 */
export function reconcileChatPage<T extends ReconciledChatMessage>(
  onScreen: ChatPage<T> | undefined,
  fetched: ChatPage<T>,
  now: number = Date.now(),
): ChatPage<T> {
  if (!onScreen) return fetched
  const messages = reconcilePlanChatPage(onScreen.messages, fetched.messages, now)
  const older = fetched.hasMore ? olderHistory(onScreen.messages, fetched.messages, now) : []
  if (older.length === 0) return { ...fetched, messages }
  return {
    ...fetched,
    messages: [...older, ...messages],
    nextCursor: onScreen.nextCursor,
    hasMore: onScreen.hasMore,
  }
}

type MembershipLine = {
  id: string
  messageType: string
  deletedAt: string | null
  system: Pick<ChatSystemMeta, 'kind' | 'actorId' | 'actorName'> | null
}

function joinerName(line: MembershipLine, viewerId: string | null): string | null {
  if (line.messageType !== 'system' || line.system?.kind !== 'joined_plan') return null
  if (!line.system.actorId || line.system.actorId === viewerId) return null
  return line.system.actorName?.trim() || null
}

/**
 * Joins that follow each other collapse into the first line ("Max, Sophie und Lena sind dabei"):
 * `bundles` maps that line to every name, `hidden` holds the lines it stands in for. A deleted row
 * in between is skipped; any other row, or the viewer's own join, ends the run.
 */
export function bundleMembershipLines<T extends MembershipLine>(
  messages: T[],
  viewerId: string | null,
): { hidden: Set<string>; bundles: Map<string, string[]> } {
  const runs: { first: string; names: string[]; rest: string[] }[] = []
  let open = false
  for (const message of messages) {
    if (message.deletedAt) continue
    const name = joinerName(message, viewerId)
    if (name == null) {
      open = false
      continue
    }
    const run = open ? runs[runs.length - 1] : undefined
    if (run) {
      run.names.push(name)
      run.rest.push(message.id)
    } else {
      runs.push({ first: message.id, names: [name], rest: [] })
      open = true
    }
  }
  const hidden = new Set<string>()
  const bundles = new Map<string, string[]>()
  for (const run of runs) {
    if (run.names.length < 2) continue
    bundles.set(run.first, run.names)
    for (const id of run.rest) hidden.add(id)
  }
  return { hidden, bundles }
}

export const chatMessagesResponseSchema = z.object({
  messages: z.array(chatMessageDtoSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
})

/** The pinned messages of a plan chat (most recent pin first). */
export const planChatPinsResponseSchema = z.object({
  messages: z.array(chatMessageDtoSchema),
})

export type PlanChatPinsResponse = z.infer<typeof planChatPinsResponseSchema>

export type ChatMessagesResponse = z.infer<typeof chatMessagesResponseSchema>

export const sendMessageResponseSchema = z.object({
  message: chatMessageDtoSchema,
})

export type SendMessageResponse = z.infer<typeof sendMessageResponseSchema>
